import { trainingRole } from '@core/shared/stages';
import type { Rung } from '../engine/ladder';
import type { UserDoc } from '../engine/types';
import { isHybrid } from '../engine/ladder';

const STATE_LABEL: Record<Rung['state'], string> = {
  passed: 'Passed',
  available: 'Start',
  locked: 'Locked',
  expired: 'Expired — retake',
  awaiting_content: 'Not ready yet',
};

const pct = (n: number | null | undefined) =>
  typeof n === 'number' ? `${Math.round(n * 100)}%` : '—';

/**
 * What a rung is, before you open it (§6.5).
 *
 * A lesson that teaches, a drill that repeats, a call that runs start to
 * finish: three different amounts of time and attention, and until you tap one
 * they all look the same. Somebody with five minutes before a shift should be
 * able to tell which of those they can finish.
 */
const KIND_LABEL: Record<Rung['kind'], string> = {
  knowledge: 'Lesson + check',
  drill: 'Drill',
  simulation: 'Full call',
};

/**
 * §19 — a visible six-rung progression on the rep's home screen. Locked rungs
 * are visibly locked, and an expired compliance rung is visibly distinct from
 * one that was never passed.
 */
export function StageLadder({
  rungs, user, onOpen,
}: {
  rungs: Rung[];
  user: UserDoc | null;
  onOpen: (rung: Rung) => void;
}) {
  // A rep's salesRole, or the track a manager or admin is rehearsing on.
  const track = trainingRole(user);
  // §6.10 — the ladder is the progression; the advanced tracks sit apart from
  // it. Counting them into "x of y stages passed" would make certification look
  // unfinished to a rep who is certified.
  const ladder = rungs.filter((r) => !r.advanced);
  const advanced = rungs.filter((r) => r.advanced);
  const passedCount = ladder.filter((r) => r.state === 'passed').length;

  return (
    <section className="ladder">
      <header className="ladder-head">
        <div>
          <h1>{user?.displayName ?? 'Your progress'}</h1>
          <p className="muted">
            {track ? `${track} track` : 'No sales role assigned'}
            {isHybrid(user) && ' · certified hybrid'}
          </p>
        </div>
      </header>

      {/* How far along, in one line and one bar. Six rungs down a phone is more
          scrolling than a glance, and a ladder with no sense of distance
          travelled is a list of things not done yet. */}
      {ladder.length > 0 && (
        <div className="ladder-progress">
          <div className="progress" role="progressbar"
            aria-valuenow={passedCount} aria-valuemin={0} aria-valuemax={ladder.length}
            aria-label={`${passedCount} of ${ladder.length} stages passed`}>
            <div className="progress-fill"
              style={{ width: `${(passedCount / ladder.length) * 100}%` }} />
          </div>
          <p className="small muted">
            {passedCount} of {ladder.length} stages passed
          </p>
        </div>
      )}

      <ol className="rungs">
        {ladder.map((rung) => {
          const actionable = rung.state === 'available' || rung.state === 'expired';
          const passedAgain = rung.state === 'passed' && rung.contentId;
          return (
            <li key={rung.stage} className={`rung rung-${rung.state}`}>
              <span className="rung-index" aria-hidden="true">{rung.index + 1}</span>
              <div className="rung-body">
                <h2>{rung.label}</h2>
                {/* A locked rung says its name and nothing else. It used to
                    carry the same title, blurb, an unlock sentence and a pill —
                    four signals for "not yet" — on every one of the five rungs
                    a new rep cannot touch, which buries the single thing they
                    can do under everything they cannot. */}
                {rung.state === 'locked'
                  ? <p className="lock-note">Locked</p>
                  : (
                    <>
                      <p className="muted">{rung.blurb}</p>
                      <p className="kind">{KIND_LABEL[rung.kind]}</p>
                    </>
                  )}
                {rung.progress && rung.progress.attempts > 0 && (
                  <p className="small muted">
                    {rung.progress.attempts} attempt{rung.progress.attempts === 1 ? '' : 's'}
                    {' · best '}{pct(rung.progress.bestScore)}
                  </p>
                )}
                {rung.state === 'awaiting_content' && (
                  <p className="small muted">No exercises have been published for this stage yet.</p>
                )}

              </div>
              {/* No action row for a rung there is no action on. On a phone the
                  action drops to its own full-width line, so a "Locked" pill
                  cost a locked rung as much height as a live one. */}
              {rung.state !== 'locked' && (
              <div className="rung-action">
                {actionable && rung.contentId ? (
                  <button className="primary" onClick={() => onOpen(rung)}>
                    {STATE_LABEL[rung.state]}
                  </button>
                ) : passedAgain ? (
                  <button className="ghost" onClick={() => onOpen(rung)}>Practice again</button>
                ) : (
                  <span className={`pill pill-${rung.state}`}>{STATE_LABEL[rung.state]}</span>
                )}
              </div>
              )}
            </li>
          );
        })}
      </ol>

      {/* §6.10 — after the ladder, and visibly not part of it. These are for
          reps who are already certified, so presenting them as unfinished
          business would misread the one thing the ladder is for. */}
      {advanced.length > 0 && (
        <section className="advanced">
          <h3>After certification</h3>
          <p className="small muted">
            Not required to work leads. This is where the techniques behind the
            calls get explained, and where the line each one crosses is drawn.
          </p>
          <ol className="rungs">
            {advanced.map((rung) => (
              <li key={rung.stage} className={`rung rung-${rung.state}`}>
                <span className="rung-index" aria-hidden="true">★</span>
                <div className="rung-body">
                  <h2>{rung.label}</h2>
                  {rung.state === 'locked'
                    ? <p className="lock-note">Unlocked by certifying</p>
                    : <p className="muted">{rung.blurb}</p>}
                </div>
                {rung.state !== 'locked' && (
                  <div className="rung-action">
                    <button className="ghost" onClick={() => onOpen(rung)}>
                      {rung.state === 'passed' ? 'Revisit' : 'Start'}
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ol>
        </section>
      )}
    </section>
  );
}
